/**
 * 任务详情 —— 单元、证据都来自台账本身。
 *
 * 去掉了原型的「历史」页签:那四条时间线是写死的字符串。台账确实有事件流
 * (hub 每次变更都 /ws 广播 ledger 事件),但它不持久化成可回溯的历史表,
 * 所以这里不假装有历史,只显示台账真存的东西。
 */
import { useState } from 'react';
import { createPortal } from 'react-dom';
import type { Task, TaskUnit } from '@/types/fleet';
import { NA } from '@/lib/format';

interface Props { task: Task; units: TaskUnit[]; onClose: () => void; }

const statusLabels: Record<string, string> = { unclaimed: '待认领', claimed: '已认领', in_progress: '进行中', split: '已拆分', completed: '已完成', failed: '已失败' };
const statusColors: Record<string, string> = { unclaimed: 'text-foreground-400 bg-foreground-100', claimed: 'text-secondary-700 bg-secondary-100', in_progress: 'text-accent-700 bg-accent-100', split: 'text-foreground-500 bg-background-200', completed: 'text-green-700 bg-green-100', failed: 'text-red-700 bg-red-100' };

const fmt = (ts: string | null) => (ts ? new Date(ts).toLocaleString('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : NA);

function prettyEvidence(raw?: string): string | null {
  if (!raw) return null;
  try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
}

export default function TaskDetailPanel({ task, units, onClose }: Props) {
  const [tab, setTab] = useState<'units' | 'evidence'>('units');
  const withEvidence = units.filter((u) => u.evidenceRaw || u.result);

  // 覆盖层一律 portal 到 body。DeviceDetailSheet 那次实测过后果: 祖先只要带 transform
  // (哪怕是 animate-fade-in-up 结束后残留的单位矩阵)就会创建包含块, position:fixed
  // 于是相对那个盒子而不是视口 —— 抽屉被压成 322px 高。这里目前没有那样的祖先,
  // 但那是【别人现在没加动画】而已, 不是这个组件自己站得住。
  return createPortal(
    <div className="fixed inset-y-0 right-0 z-40 w-[480px] max-w-full bg-background-50 border-l border-background-200/70 shadow-lg flex flex-col">
      <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-background-200/70">
        <div className="min-w-0">
          <h2 className="text-sm font-bold text-foreground-900 break-words">{task.name}</h2>
          <p className="text-[10px] text-foreground-500 mt-0.5 font-mono">任务 #{task.id} · {task.assignees.length ? task.assignees.join('、') : '尚无认领者'}</p>
        </div>
        <button onClick={onClose} className="w-9 h-9 shrink-0 flex items-center justify-center rounded hover:bg-background-100 cursor-pointer"><i className="ri-close-line text-foreground-500"></i></button>
      </div>

      <div className="px-4 py-3 border-b border-background-200/70">
        <div className="flex items-center gap-4 text-[11px] text-foreground-500 mb-2">
          <span>创建: {fmt(task.created || null)}</span>
          {/* 台账没有截止时间 —— 显示 NA 而不是把创建时间当截止画上去 */}
          <span>截止: {NA}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-background-200 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${task.progress === 100 ? 'bg-green-500' : 'bg-primary-500'}`} style={{ width: `${task.progress}%` }} />
          </div>
          <span className="text-[11px] font-mono font-bold text-foreground-700">{task.progress}%</span>
        </div>
        <p className="text-[10px] text-foreground-400 mt-1">进度是数出来的:已完成单元 / 可完成单元。</p>
      </div>

      <div className="flex border-b border-background-200/70">
        {([['units', `工作单元 (${units.length})`], ['evidence', `证据 (${withEvidence.length})`]] as const).map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} className={`flex-1 py-2.5 text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${tab === k ? 'text-foreground-900 border-b-2 border-primary-500' : 'text-foreground-400 hover:text-foreground-600'}`}>{label}</button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {tab === 'units' && (
          <div className="space-y-2">
            {units.length === 0 && <p className="text-xs text-foreground-400 text-center py-10">这个任务没有工作单元。</p>}
            {units.map((u) => (
              <div key={u.id} className="p-3 bg-background-50 border border-background-200/70 rounded-md">
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <span className="text-xs font-medium text-foreground-800 break-words">{u.name}</span>
                  <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded ${statusColors[u.status]}`}>{statusLabels[u.status]}</span>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-foreground-500">
                  <span className="font-mono">#{u.id}</span>
                  <span>{u.assignee ? `认领: ${u.assignee}` : '尚未认领'}</span>
                  {u.failReason && <span className="text-red-600">失败: {u.failReason}</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'evidence' && (
          withEvidence.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-foreground-400">
              <i className="ri-file-search-line text-xl text-foreground-300 mb-2" />
              <p className="text-xs">还没有单元提交过证据</p>
              <p className="text-[10px] mt-1">台账要求完成必须带证据 —— 没有证据就不算完成。</p>
            </div>
          ) : (
            <div className="space-y-3">
              {withEvidence.map((u) => (
                <div key={u.id} className="bg-background-100 rounded-md p-3">
                  <div className="text-[11px] font-medium text-foreground-700 mb-1">{u.name}{u.assignee ? ` · ${u.assignee}` : ''}</div>
                  {u.result && <div className="text-xs text-foreground-800 mb-1.5">{u.result}</div>}
                  {u.evidenceRaw && (
                    <pre className="text-[10px] font-mono text-foreground-600 bg-background-50 rounded p-2 overflow-x-auto">{prettyEvidence(u.evidenceRaw)}</pre>
                  )}
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
    ,
    document.body,
  );
}
